export type TemplateWebMCPErrorCode =
  | 'EDITOR_NOT_READY'
  | 'INVALID_INPUT'
  | 'UNAUTHORIZED'
  | 'UNEXPECTED'
  | 'STALE_REVISION'
  | 'INVALID_TOKEN'
  | 'UNSUPPORTED'
  | 'PREVIEW_FAILED'
  | 'CAPABILITY_UNAVAILABLE'
  | 'ANIMATION_PRESET_UNKNOWN'
  | 'ANIMATION_EFFECT_NOT_FOUND'
  | 'ANIMATION_TARGET_INVALID'
  | 'ANIMATION_DUPLICATE'
  | 'ANIMATION_CONFLICT'
  | 'ANIMATION_ROLE_MISMATCH'
  | 'ANIMATION_PARAMS_INVALID'
  | 'PREVIEW_RANGE_INVALID'
  | 'PREVIEW_NOT_READY'
  | 'PREVIEW_TIMEOUT'
  | 'PREVIEW_CANCELLED'
  | 'PREVIEW_ASSETS_UNRESOLVED'
  | 'MEDIA_FORMAT_UNSUPPORTED'
  | 'ARTIFACT_TOO_LARGE'
  | 'ARTIFACT_UPLOAD_FAILED';

export type TemplateWebMCPSuccess<T> = {
  ok: true;
  data: T;
};

export type TemplateWebMCPFailure = {
  ok: false;
  error: {
    code: TemplateWebMCPErrorCode;
    message: string;
    details?: unknown;
  };
};

export type TemplateWebMCPPayload<T> = TemplateWebMCPSuccess<T> | TemplateWebMCPFailure;

export type TemplateWebMCPContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; data: string; mimeType: 'image/png' | 'image/jpeg' }
  | { type: 'resource'; resource: { uri: string; mimeType: string; blob: string } };

export type TemplateWebMCPImageAttachment = {
  base64: string;
  mimeType: 'image/png' | 'image/jpeg';
};

/** Embedded MCP resource, used for review video. */
export type TemplateWebMCPResourceAttachment = {
  kind: 'resource';
  uri: string;
  base64: string;
  mimeType: string;
};

export type TemplateWebMCPToolResult<T> = {
  content: Array<TemplateWebMCPContentBlock>;
  structuredContent: TemplateWebMCPPayload<T>;
  isError?: true;
};

export function toolSuccess<T>(data: T): TemplateWebMCPSuccess<T> {
  return { ok: true, data };
}

export function toolError(
  code: TemplateWebMCPErrorCode,
  message: string,
  details?: unknown,
): TemplateWebMCPFailure {
  return {
    ok: false,
    error: details === undefined ? { code, message } : { code, message, details },
  };
}

export const SAVE_CONFIRMATION_INSTRUCTION =
  'Ask the user to confirm this save. Call commit_working_copy again with userConfirmed true only if the user explicitly asked to save. Call it with cancel true to close Confirm Save without saving.';

function humanReadable<T>(payload: TemplateWebMCPPayload<T>): string {
  if (!payload.ok) {
    return payload.error.message;
  }

  const data = payload.data as {
    templateId?: unknown;
    readType?: unknown;
    operation?: unknown;
    saved?: unknown;
    instruction?: unknown;
  };
  if (data?.operation === 'commit_working_copy') {
    if (typeof data.instruction === 'string' && data.instruction.length > 0) {
      return data.instruction;
    }
    return data.saved === false ? 'Save was not confirmed.' : 'Saved the template.';
  }
  if (typeof data?.operation === 'string' && data.operation.length > 0) {
    return `Applied ${data.operation} successfully.`;
  }
  if (typeof data?.readType === 'string' && data.readType.length > 0) {
    if (data.readType === 'preview') {
      return 'Read preview successfully.';
    }
    return `Read ${data.readType} successfully.`;
  }
  if (typeof data?.templateId === 'string' && data.templateId.length > 0) {
    return `Read template ${data.templateId} successfully.`;
  }

  return 'Tool completed successfully.';
}

export function mcpToolResult<T>(
  payload: TemplateWebMCPPayload<T>,
  attachment?: TemplateWebMCPImageAttachment | TemplateWebMCPResourceAttachment,
): TemplateWebMCPToolResult<T> {
  // Harnesses that forward only `content` to the model never see `structuredContent`,
  // so the payload is serialized here as well. See MCP tools spec, structured content.
  const content: TemplateWebMCPContentBlock[] = [
    { type: 'text', text: humanReadable(payload) },
    { type: 'text', text: JSON.stringify(payload) },
  ];
  if (payload.ok && attachment?.base64) {
    if ('kind' in attachment) {
      content.push({
        type: 'resource',
        resource: { uri: attachment.uri, mimeType: attachment.mimeType, blob: attachment.base64 },
      });
    } else {
      content.push({ type: 'image', data: attachment.base64, mimeType: attachment.mimeType });
    }
  }
  if (payload.ok) {
    return { content, structuredContent: payload };
  }
  return { content, structuredContent: payload, isError: true };
}
